import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, toRef, watch } from "vue";
import BaseButtonV2 from "@/components-UI/buttons/BaseButtonV2.vue";
import ActionButton from "@/components-UI/buttons/ActionButton.vue";
import BaseModal from "@/components-UI/modals/BaseModal.vue";
import { IContestCashbackOffer } from "@/components_v3/affiliate/contest/entities/cashback-offer/model/types";
import { useContestOfferEditor } from "@/components_v3/affiliate/contest/model/useContestOfferEditor";
import {
    getAffiliateCashbackOffersEnriched,
    resetContestOfferStatusesCache,
} from "@/components_v3/affiliate/contest/shared/api/contest-cashback.api";
import ContestOfferSection from "@/components_v3/affiliate/contest/widgets/contest-section/ui/ContestOfferSection.vue";
import { Affiliate } from "@/services/affiliates/Affiliate";
import { showServerError } from "@/utils";
import SvgReset from "@/assets/icons/reload.svg";
import SvgTrash from "@/assets/icons/trash.svg";

interface Props {
    affiliate: Affiliate;
    month: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AffiliateContestTab',
  props: {
    affiliate: null,
    month: null
  },
  emits: ["update:loading"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'update:loading', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    affiliate: Affiliate;
    month: string;
}

const LOAD_OFFERS_ERROR_MESSAGE = 'Не удалось загрузить офферы';





const month = toRef(props, 'month');
const offers = ref<IContestCashbackOffer[]>([]);
const offerToDelete = ref<IContestCashbackOffer | null>(null);
const isListLoading = ref(false);
const loadError = ref<string | null>(null);

const affiliateId = computed(() => props.affiliate?.id);
const isShowDeleteModal = computed(() => offerToDelete.value != null);

const isInitialLoading = computed(() => isListLoading.value && offers.value.length === 0);
const showFatalError = computed(
    () => !isListLoading.value && !!loadError.value && offers.value.length === 0,
);
const showSections = computed(() => !isInitialLoading.value && !showFatalError.value);

const myChoiceOffers = computed(() => offers.value.filter((offer) => offer.createdByAffiliate));
const superCashbackOffers = computed(() => offers.value.filter((offer) => !offer.createdByAffiliate));

async function loadOffers(): Promise<void> {
    if (!affiliateId.value) {
        return;
    }

    isListLoading.value = true;

    try {
        const loaded = await getAffiliateCashbackOffersEnriched(affiliateId.value, month.value);
        offers.value = loaded;
        loadError.value = null;
    } catch (err) {
        showServerError(err, LOAD_OFFERS_ERROR_MESSAGE);

        if (!offers.value.length) {
            loadError.value = LOAD_OFFERS_ERROR_MESSAGE;
        }
    } finally {
        isListLoading.value = false;
    }
}

const {
    draft,
    isSubmitting,
    isGoalsLoading,
    canSave,
    isEditingOffer,
    isAddActiveInSection,
    startAdd,
    startEdit,
    cancel,
    onOfferIdChange,
    updatePlan,
    updateGoals,
    updateSpecialPeriodEnabled,
    updateSpecialPeriod,
    save,
    deleteOffer,
    resetEditor,
} = useContestOfferEditor({
    affiliateId,
    month,
    offers,
    reloadOffers: loadOffers,
});

function hideDeleteModal(): void {
    offerToDelete.value = null;
}

function startDelete(offer: IContestCashbackOffer): void {
    resetEditor();
    offerToDelete.value = offer;
}

async function confirmDelete(): Promise<void> {
    const offer = offerToDelete.value;

    if (!offer) {
        return;
    }

    const isDeleted = await deleteOffer(offer);

    if (isDeleted) {
        hideDeleteModal();
    }
}

function onStartEdit(offer: IContestCashbackOffer): void {
    hideDeleteModal();
    startEdit(offer);
}

function onStartAdd(createdByAffiliate: boolean): void {
    hideDeleteModal();
    startAdd(createdByAffiliate);
}

function resetListForContextChange(): void {
    hideDeleteModal();
    offers.value = [];
    loadError.value = null;
    resetContestOfferStatusesCache();
    resetEditor();
}

watch(isListLoading, (value) => {
    emit('update:loading', value);
}, { immediate: true });

watch(month, (_newMonth, oldMonth) => {
    if (oldMonth !== undefined) {
        resetListForContextChange();
    }

    void loadOffers();
});

watch(affiliateId, (_newId, oldId) => {
    if (oldId !== undefined) {
        resetListForContextChange();
    }

    void loadOffers();
});

onMounted(() => {
    void loadOffers();
});

onBeforeUnmount(() => {
    hideDeleteModal();
    resetEditor();
    resetContestOfferStatusesCache();
    emit('update:loading', false);
});

return { __sfc: true,LOAD_OFFERS_ERROR_MESSAGE, props, emit, month, offers, offerToDelete, isListLoading, loadError, affiliateId, isShowDeleteModal, isInitialLoading, showFatalError, showSections, myChoiceOffers, superCashbackOffers, loadOffers, draft, isSubmitting, isGoalsLoading, canSave, isEditingOffer, isAddActiveInSection, startAdd, startEdit, cancel, onOfferIdChange, updatePlan, updateGoals, updateSpecialPeriodEnabled, updateSpecialPeriod, save, deleteOffer, resetEditor, hideDeleteModal, startDelete, confirmDelete, onStartEdit, onStartAdd, resetListForContextChange, BaseButtonV2, ActionButton, BaseModal, ContestOfferSection, SvgReset, SvgTrash }
}

})